import React, { useCallback, useEffect, useState } from 'react'
import { Heading, Text } from '../../components/ui'
import { PermissionGuard } from '../../components/PermissionGuard'
import { usePermission } from '../../hooks/usePermissions'
import { useUserLogs } from '../../hooks/useUserLogs'
import { modulesService } from '../../services/modules'
import { estoqueService } from '../../services/estoque'
import type {
  EstoqueCategoria,
  EstoqueItem,
  EstoqueLocal,
  EstoqueSaldo,
} from '../../types/estoque'
import { MODULE_NAME, PERM_LOADING, tabButtonClass } from './estoqueUi'
import EstoqueSaldosTab from './EstoqueSaldosTab'
import EstoqueHistoricoTab from './EstoqueHistoricoTab'
import EstoqueItensTab from './EstoqueItensTab'
import EstoqueCategoriasTab from './EstoqueCategoriasTab'
import EstoqueLocaisTab from './EstoqueLocaisTab'
import EstoqueMovimentacaoModal from './EstoqueMovimentacaoModal'

type Tab = 'estoque' | 'historico' | 'itens' | 'categorias' | 'locais'

const Gestao: React.FC = () => {
  const { hasPermission: canInsert } = usePermission(MODULE_NAME, 'insert')
  const { hasPermission: canUpdate } = usePermission(MODULE_NAME, 'update')
  const { hasPermission: canDelete } = usePermission(MODULE_NAME, 'delete')
  const { logView } = useUserLogs()

  const [moduleTitle, setModuleTitle] = useState('Estoque')
  const [moduleDescription, setModuleDescription] = useState(
    'Gestão de saldos, itens, categorias e locais de estoque'
  )
  const [activeTab, setActiveTab] = useState<Tab>('estoque')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [itens, setItens] = useState<EstoqueItem[]>([])
  const [categorias, setCategorias] = useState<EstoqueCategoria[]>([])
  const [locais, setLocais] = useState<EstoqueLocal[]>([])
  const [saldos, setSaldos] = useState<EstoqueSaldo[]>([])

  const [movementOpen, setMovementOpen] = useState(false)
  const [movementItemId, setMovementItemId] = useState('')
  const [movementLocalId, setMovementLocalId] = useState('')

  useEffect(() => {
    modulesService.getByName(MODULE_NAME).then((result) => {
      if (result.success && result.data) {
        if (result.data.display_name) setModuleTitle(result.data.display_name)
        if (result.data.description) setModuleDescription(result.data.description)
      }
    })
  }, [])

  const loadAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [itensData, categoriasData, locaisData, saldosData] = await Promise.all([
        estoqueService.listItens(true),
        estoqueService.listCategorias(true),
        estoqueService.listLocais(true),
        estoqueService.listSaldos(),
      ])
      setItens(itensData)
      setCategorias(categoriasData)
      setLocais(locaisData)
      setSaldos(saldosData)
    } catch (err) {
      console.error(err)
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados do estoque.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadAll()
  }, [loadAll])

  useEffect(() => {
    void logView('marketing_stock_item', null, 'Visualizou tela de estoque de marketing')
  }, [logView])

  const openMovimentar = (itemId?: string, localId?: string) => {
    setMovementItemId(itemId || '')
    setMovementLocalId(localId || '')
    setMovementOpen(true)
  }

  return (
    <div className="flex-1 flex flex-col overflow-x-hidden overflow-y-auto">
      <PermissionGuard
        moduleName={MODULE_NAME}
        permissionType="show"
        loadingComponent={PERM_LOADING}
        fallback={
          <div className="flex flex-1 items-center justify-center">
            <div className="text-center">
              <Text size="xl" className="mb-2 text-gray-500 dark:text-gray-400">
                Acesso Negado
              </Text>
              <Text size="sm" color="muted">
                Você não tem permissão para acessar esta página.
              </Text>
            </div>
          </div>
        }
      >
        <div className="py-6 px-4 sm:px-6 lg:px-8">
          <div className="mb-6">
            <Heading as="h1" size="3xl" className="text-gray-900 dark:text-white">
              {moduleTitle}
            </Heading>
            <Text size="sm" color="muted" className="mt-2">
              {moduleDescription}
            </Text>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              {error}
            </div>
          )}

          <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex flex-wrap gap-x-8 gap-y-2" aria-label="Abas de estoque">
              <button type="button" className={tabButtonClass(activeTab === 'estoque')} onClick={() => setActiveTab('estoque')}>
                Estoque
              </button>
              <button type="button" className={tabButtonClass(activeTab === 'historico')} onClick={() => setActiveTab('historico')}>
                Histórico
              </button>
              <button type="button" className={tabButtonClass(activeTab === 'itens')} onClick={() => setActiveTab('itens')}>
                Itens
              </button>
              <button type="button" className={tabButtonClass(activeTab === 'categorias')} onClick={() => setActiveTab('categorias')}>
                Categorias
              </button>
              <button type="button" className={tabButtonClass(activeTab === 'locais')} onClick={() => setActiveTab('locais')}>
                Local de Estoque
              </button>
            </nav>
          </div>

          {activeTab === 'estoque' && (
            <EstoqueSaldosTab
              itens={itens}
              saldos={saldos}
              locais={locais}
              categorias={categorias}
              loading={loading}
              onMovimentar={openMovimentar}
              canUpdate={canUpdate}
            />
          )}

          {activeTab === 'historico' && <EstoqueHistoricoTab items={itens} locais={locais} />}

          {activeTab === 'itens' && (
            <EstoqueItensTab
              itens={itens}
              categorias={categorias}
              loading={loading}
              onChanged={loadAll}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          )}

          {activeTab === 'categorias' && (
            <EstoqueCategoriasTab
              categorias={categorias}
              loading={loading}
              onChanged={loadAll}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          )}

          {activeTab === 'locais' && (
            <EstoqueLocaisTab
              locais={locais}
              loading={loading}
              onChanged={loadAll}
              canInsert={canInsert}
              canUpdate={canUpdate}
              canDelete={canDelete}
            />
          )}
        </div>

        <EstoqueMovimentacaoModal
          open={movementOpen}
          items={itens}
          locais={locais}
          initialItemId={movementItemId}
          initialLocalId={movementLocalId}
          onClose={() => setMovementOpen(false)}
          onSaved={loadAll}
        />
      </PermissionGuard>
    </div>
  )
}

export default Gestao
